import React from 'react';

export interface IconProps {
    size?: number;
    strokeWidth?: number;
    className?: string;
    style?: React.CSSProperties;
}

export type IconComponent = React.ComponentType<IconProps>;

const DEFAULT_SIZE = 20;

const makeIcon = (children: React.ReactNode, defaultStrokeWidth = 2): IconComponent =>
    function Icon({ size = DEFAULT_SIZE, strokeWidth = defaultStrokeWidth, className, style }: IconProps) {
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                className={className}
                style={style}
            >
                {children}
            </svg>
        );
    };

/* ── Nav / home-tile icons (used by NeighborsSidenav, home grid, SidebarIcon) ── */
export const MenuIcon = makeIcon((
    <>
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
    </>
), 1.8);

export const SocialIcon = makeIcon(
    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
    1.8,
);

export const CalendarIcon = makeIcon((
    <>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </>
), 1.8);

export const OrderIcon = makeIcon(
    <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />,
    1.8,
);

export const PaymentIcon = makeIcon((
    <>
        <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
    </>
), 1.8);

export const DocumentsIcon = makeIcon((
    <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
    </>
), 1.8);

export const LinksIcon = makeIcon((
    <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </>
), 1.8);

export const HelloIcon = makeIcon(
    <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    1.8,
);

export const GreetingsIcon = makeIcon(
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />,
    1.8,
);

export const SurveyIcon = makeIcon(
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />,
    1.8,
);

export const StarIcon = makeIcon(
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
    1.8,
);

export const HomeIcon = makeIcon((
    <>
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
        <path d="M9 21V12h6v9" />
    </>
), 1.8);

// Also used for "My Docs" (Documents page) and the "Group"/"Service" connection chips —
// the four-limb person silhouette reads as both "profile" and "person/people" at a glance.
export const ProfileIcon = makeIcon((
    <>
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </>
), 1.8);

/* ── Documents page icons ── */
export const PdfIcon = makeIcon((
    <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="15" y2="17" />
        <line x1="9" y1="9" x2="11" y2="9" />
    </>
), 1.8);

export const WordIcon = makeIcon((
    <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
        <line x1="8" y1="9" x2="16" y2="9" />
    </>
), 1.8);

export const ImageIcon = makeIcon((
    <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </>
), 1.8);

// Plain file outline — used for the default doc-type icon, the "All Documents" sidebar
// entry, and (at a larger size/lighter stroke) the empty-state illustration.
export const FileIcon = makeIcon((
    <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </>
), 1.8);

export const OpenIcon = makeIcon((
    <>
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
    </>
), 2.5);

export const TrashIcon = makeIcon((
    <>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </>
), 2);

export const ChevronIcon = makeIcon(<polyline points="6 9 12 15 18 9" />, 2.5);

export const ClockIcon = makeIcon((
    <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </>
), 2);

// Two-person "community/group" glyph — shared by the Community sidebar entry and the
// Group/Service "connected to" chips on a document card.
export const UsersIcon = makeIcon((
    <>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
    </>
), 2);

export const GroupDocsIcon = makeIcon((
    <>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    </>
), 2);

/* ── Links page icons ── */
export const ChainLinkIcon = makeIcon((
    <>
        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </>
), 2);

export const SearchIcon = makeIcon((
    <>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>
), 2.2);

/* ── Upload document modal icons ── */
export const UploadIcon = makeIcon((
    <>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </>
), 2.2);

export const CloseIcon = makeIcon((
    <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </>
), 2.4);

export const ArrowIcon = makeIcon((
    <>
        <line x1="7" y1="17" x2="17" y2="7" />
        <polyline points="7 7 17 7 17 17" />
    </>
), 2.5);

export const HamburgerIcon = makeIcon((
    <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </>
), 2);

/* ── Home page emoji replacements ── */

// Filled map pin. Not built with makeIcon because that helper always strokes an outline.
export const PinFilledIcon = ({ size = 13, className, style }: IconProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
        className={className}
        style={style}
    >
        <path d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z" />
    </svg>
);

export const PinOutlineIcon = makeIcon((
    <>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
        <circle cx="12" cy="10" r="3" />
    </>
), 2.2);

export const NewspaperIcon = makeIcon((
    <>
        <path d="M4 22h14a2 2 0 002-2V5a1 1 0 00-1-1H5a1 1 0 00-1 1v15a2 2 0 01-2 2h2z" />
        <path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" />
    </>
), 2);

export const ClipboardCheckIcon = makeIcon((
    <>
        <path d="M9 2h6a1 1 0 011 1v2H8V3a1 1 0 011-1z" />
        <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
        <polyline points="9 13 11 15 15 11" />
    </>
), 2);
